import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function OrgProfile() {
  const [p, setP] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const load = async () => { const { data } = await api.get("/org/profile"); setP(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        company_name: p.company_name,
        address: p.address,
        tax_id: p.tax_id,
        contact_email: p.contact_email,
        contact_phone: p.contact_phone,
        primary_color: p.primary_color,
      };
      await api.patch("/org/profile", payload);
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    try {
      const fd = new FormData(); fd.append("file", logoFile);
      const { data } = await api.post("/vendor/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.logo_url || data.url;
      await api.patch("/org/profile", { logo_url: url });
      toast.success("Logo uploaded");
      setLogoFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!p) return <div className="p-4 sm:p-6 lg:p-8" data-testid="org-profile-page"><div className="surface-card p-8 text-center text-muted-foreground">Loading…</div></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="org-profile-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Company profile</h1>
        <p className="text-muted-foreground">Your organization’s public identity — name, contact, GST, logo, branding.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface-card p-6 space-y-4">
          <h2 className="font-display text-lg font-black">Details</h2>
          <div className="space-y-2"><Label>Company name</Label><Input value={p.company_name || ""} onChange={(e) => setP({ ...p, company_name: e.target.value })} data-testid="profile-company" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Contact email</Label><Input value={p.contact_email || ""} onChange={(e) => setP({ ...p, contact_email: e.target.value })} data-testid="profile-email" /></div>
            <div className="space-y-2"><Label>Contact phone</Label><Input value={p.contact_phone || ""} onChange={(e) => setP({ ...p, contact_phone: e.target.value })} data-testid="profile-phone" /></div>
          </div>
          <div className="space-y-2"><Label>Address</Label><Input value={p.address || ""} onChange={(e) => setP({ ...p, address: e.target.value })} data-testid="profile-address" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>GSTIN / Tax ID</Label><Input value={p.tax_id || ""} onChange={(e) => setP({ ...p, tax_id: e.target.value })} data-testid="profile-gst" /></div>
            <div className="space-y-2"><Label>Brand color</Label><Input value={p.primary_color || ""} onChange={(e) => setP({ ...p, primary_color: e.target.value })} placeholder="#C85A32" /></div>
          </div>
          <Button onClick={save} data-testid="profile-save">Save changes</Button>
        </div>

        <div className="surface-card p-6 space-y-4">
          <h2 className="font-display text-lg font-black">Logo</h2>
          <div className="aspect-video w-full rounded-2xl border border-dashed border-border bg-muted/30 grid place-items-center overflow-hidden">
            {p.logo_url ? <img src={p.logo_url.startsWith("http") ? p.logo_url : `${process.env.REACT_APP_BACKEND_URL}${p.logo_url}`} alt="logo" className="max-h-32 object-contain" /> : <span className="text-sm text-muted-foreground">No logo uploaded</span>}
          </div>
          <Input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="profile-logo-input" />
          <Button onClick={uploadLogo} disabled={!logoFile} data-testid="profile-logo-upload">Upload new logo</Button>
        </div>
      </div>
    </div>
  );
}
